import React from 'react';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { MegaMenuProvider } from './contexts/MegaMenuContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppContent } from './AppContent';
import { SidePanelProvider } from './contexts/SidePanelContext';
import { MetadataProvider } from './contexts/MetadataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ServiceProvider } from './contexts/ServiceContext';
import GlobalLogoutListener from './components/common/GlobalLogoutListener';
import InactivityTimer from './components/common/InactivityTimer';
import { BrandProvider } from './contexts/BrandingContext';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ModalProvider } from './contexts/ModalContext';
import { ResoProvider } from './contexts/ResoContext';
import { LoaderProvider } from './contexts/LoaderContext';
import AuthCoordinator from './contexts/AuthCoordinator';
import { DebuggerProvider } from './contexts/DebuggerContext';
import { ScrollProvider } from './contexts/ScrollProvider';
import { suppressUnwantedLogs } from './suppressLogs';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { TenantProvider } from './contexts/TenantContext';
import { DefaultsProvider } from './contexts/DefaultsContext';
import { HeaderProvider } from './contexts/HeaderContext';

suppressUnwantedLogs();

const queryClient = new QueryClient();

const AuthenticatedApp: React.FC = () => {
  const { logout } = useAuth();
  return (
    <>
      <InactivityTimer />
      <ServiceProvider>
        <LoaderProvider>
          <CustomThemeProvider>
            <HeaderProvider>
              <ModalProvider>
                <SidePanelProvider>
                  <MegaMenuProvider>
                    <DefaultsProvider>
                      <AppContent />
                    </DefaultsProvider>
                  </MegaMenuProvider>
                </SidePanelProvider>
              </ModalProvider>
            </HeaderProvider>
          </CustomThemeProvider>
        </LoaderProvider>
      </ServiceProvider>

      <GlobalLogoutListener onCustomLogout={logout} />
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ScrollProvider>
        <DebuggerProvider>
          <TenantProvider>
            <BrandProvider>
              <FeatureFlagsProvider>
                <AnalyticsProvider>
                  <AuthProvider>
                    <ResoProvider>
                      <MetadataProvider>
                        {/* AuthenticationCoordinator now manages the entire auth flow */}
                        <AuthCoordinator
                          onStateChange={(state) => {
                            // console.log('Auth state:', state);
                          }}
                        >
                          <AuthenticatedApp></AuthenticatedApp>
                        </AuthCoordinator>
                      </MetadataProvider>
                    </ResoProvider>
                  </AuthProvider>
                </AnalyticsProvider>
              </FeatureFlagsProvider>
            </BrandProvider>
          </TenantProvider>
        </DebuggerProvider>
      </ScrollProvider>
    </QueryClientProvider>
  );
}

export default App;
